import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  IconButton,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Tooltip,
  Alert,
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  DetailAndDelBtn,
  DownArrowIcon,
  EyeIcon,
  NEW,
  PLANT_TYPES,
  cardClasses,
  cropClasses,
  cropplanStrainEndpoint,
  errFeedbackFn,
  miscClasses,
} from '../../utility';
import CropRunFRTLPopup from '../../utility/popups/CropRunFRTLPopup';
import APIService from '../../../api/apiService';
import { FRTLContext } from '../../../context/FRTLContext';
import { useParams } from 'react-router';
import { fetchFrtlData } from '../../../api/apiUtils';
import { getFrtlAliasString } from '../../../utility/frtlUtils';

const tableTitles = [
  { title: 'Batch Name', key: 'name' },
  { title: 'Mother FRTL', key: 'frtl' },
  { title: 'Mother Plant Tag', key: 'mp_tag_number', defShow: true },
  { title: 'Package ID', key: 'package_id', defShow: true },
  { title: 'From Type', key: 'plant_type' },
  { title: 'Plant Count', key: 'plant_count' },
  { title: 'Quality Plan', key: 'qualityplan' },
  { title: 'Phase Plan', key: 'phaseplan' },
  { title: 'Target Subphase', key: 'target_subphase' },
  { title: 'Target Subphase Date', key: 'target_subphase_date' },
  { title: 'General Functions', key: 'action' },
];

export function StrainAccordion(props) {
  const {
    strain,
    currDarkThemeClass,
    selectedFarmId,
    values,
    handleSetValues,
    qualityPlansList = [],
    qualityPlansLists = [],
    phasePlansList = [],
    tragetSubphasesList = [],
    handleErrorWithFeedback,
    cropPlanPerms,
    phasePlans
  } = props;
  const [toExpand, setToExpand] = useState(true);
  const [frtlPopupOpen, setFrtlPopupOpen] = useState(false);
  const [frtlPopupDetailChildren, setFrtlPopupDetailChildren] = useState({});
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
  const { cropplanid } = useParams();

  useEffect(() => {
    if (!frtlContextValue) {
      fetchFrtlData()
        .then(data => {
          setFrtlContextValue(data || {});
        })
        .catch(error => {
          console.error('Error loading FRTL data:', error);
          setFrtlContextValue({});
        });
    }
  }, [frtlContextValue, setFrtlContextValue]);

  const handleFrtlPopupOpen = (batch_id, frtl) => {
    const [farm, room, table, level] = frtl?.split('.').map(e => +e ?? e) ?? [props?.farm?.id ?? '', props?.room ?? '', '', ''];
    setFrtlPopupOpen(true);
    setFrtlPopupDetailChildren({
      currDarkThemeClass,
      farm,
      room,
      table,
      level,
      selectedFarmId,
      handleEditFrtl: value => handleBatchLevelValues(batch_id, 'frtl', value),
    });
  };

  const handleFrtlPopupClose = () => {
    setFrtlPopupOpen(false);
  };

  const plantCount = useMemo(() => strain?.batches?.reduce((acc, curr) => acc + +curr?.plant_count, 0), [strain]);

  // handleSetValues utility at strains level
  const handleStrainLevelValues = (prop, value) => {
    handleSetValues(
      'strains',
      values?.strains?.map(st =>
        (st?.id && st?.id === strain?.id) || (st?.tempRef && st?.tempRef === strain?.tempRef) ? { ...st, [prop]: value } : st
      )
    );
  };

  // Check if batch exists (has an ID)
  const batchExists = batch => {
    return batch?.id && !isNaN(+batch.id);
  };

  // handleSetValues utility at batch level
  const handleBatchLevelValues = (batch_id, propOrUpdates, value) => {
    // Check if the run_status allows edits (1 or 2)
    const canEdit = values?.run_status === 1 || values?.run_status === 2;

    if (!canEdit) {
      console.log('Editing not allowed with current run status:', values?.run_status);
      return;
    }

    const updates = typeof propOrUpdates === 'string' ? { [propOrUpdates]: value } : propOrUpdates;

    handleSetValues(
      'strains',
      values?.strains?.map(st =>
        (st?.id && st?.id === strain?.id) || (st?.tempRef && st.tempRef === strain?.tempRef)
          ? {
            ...st,
            batches: st.batches?.map(batch => { // Using st.batches instead of strain.batches for consistency
              const isCurrentBatch = (batch?.id && batch?.id === batch_id) || (batch?.tempRef && batch?.tempRef === batch_id);
              if (isCurrentBatch) {
                const filteredUpdates = {};
                const editableFields = ['frtl', 'plant_count', 'mp_tag_number', 'phaseplan', 'qualityplan', 'target_subphase', 'target_subphase_date'];

                Object.keys(updates).forEach(key => {
                  if (!batchExists(batch) || editableFields.includes(key)) {
                    filteredUpdates[key] = updates[key];
                  }
                });

                return { ...batch, ...filteredUpdates };
              }
              return batch;
            }),
          }
          : st
      )
    );
  };

  const titles = useMemo(
    () =>
      tableTitles.filter(
        title => !title.defShow || (title.defShow && strain?.batches?.every(batch => title.key in batch && batch?.[title.key] !== null))
      ),
    [strain]
  );

  const eCPProps = {
    className: `${cardClasses.strainAccordion?.accordion} ${currDarkThemeClass}`,
    elevation: 0,
    expanded: toExpand,
    onChange: () => setToExpand(!toExpand),
    square: true,
  };

  const eCPSummaryProps = {
    className: `${cardClasses?.strainAccordion?.accordionSummary} ${currDarkThemeClass}`,
    expandIcon: <DownArrowIcon className={`${cardClasses?.strainAccordion.accordionExpandIcon} ${currDarkThemeClass}`} />,
  };

  const tableTitleCellProps = {
    className: `${cardClasses.strainAccordion.tableTitleCell} ${currDarkThemeClass}`,
  };

  const tableTitlesList = titles.map((e, i) => (
    <TableCell key={i} {...tableTitleCellProps}>
      {e.title}
    </TableCell>
  ));

  const tableContainerProps = {
    className: `${cardClasses.strainAccordion.tableHead} ${currDarkThemeClass}`,
  };

  const textFieldProps = ({ prop, inputProps = {}, otherTextProps = {}, otherTextFieldClasses = '', batch_id = null }) => ({
    autoComplete: 'off',
    className: `${cardClasses?.strainAccordion?.textField} ${currDarkThemeClass} ${otherTextFieldClasses}`,
    disabled: values?.processing,
    inputProps,
    required: true,
    value: strain?.[prop],

    onChange: ({ target: { value } }) => handleBatchLevelValues(batch_id, prop, value),

    ...otherTextProps,
  });

  const nameProps = (batch, batch_id) => ({
    prop: 'name',
    batch_id,
    otherTextProps: {
      value: batch?.name ?? '',
    },
  });

  const getFrtlTooltipContent = frtl => {
    if (!frtlContextValue) {
      return (
        <Box className={`${cardClasses.tooltip} ${currDarkThemeClass}`}>
          <Typography>Loading FRTL data...</Typography>
        </Box>
      );
    }

    const [farmId, roomId, tableId, levelId] = (frtl?.split('.') || []).map(e => {
      const num = parseInt(e);
      return isNaN(num) ? 0 : num;
    });

    const farm = frtlContextValue.farms?.find(f => f.id === farmId) || { id: farmId, farm_name: 'Unknown Farm' };

    return (
      <Box className={`${cardClasses.tooltip} ${currDarkThemeClass}`}>
        <Typography variant="body2">
          Farm #{farm.id} {farm.farm_name}
        </Typography>
        {roomId === 0 ? (
          <Typography variant="body2">Room #0 All Rooms</Typography>
        ) : (
          <Typography variant="body2">
            {' '}
            Room #{roomId} {frtlContextValue.rooms?.find(r => r.farm_id === farmId && r.id === roomId)?.room_name || 'Unknown Room'}{' '}
          </Typography>
        )}

        {tableId === 0 ? (
          <Typography variant="body2">Table #0 All Tables</Typography>
        ) : (
          <Typography variant="body2">
            {' '}
            Table #{tableId} {frtlContextValue.tables?.find(t => t.room_id === roomId && t.id === tableId)?.name || 'Unknown Table'}{' '}
          </Typography>
        )}

        {levelId === 0 ? (
          <Typography variant="body2">Levels #0 All Levels</Typography>
        ) : (
          <Typography variant="body2">
            {' '}
            Level #{levelId} {frtlContextValue.levels?.find(l => l.roomtable_id === tableId && l.id === levelId)?.name || 'Unknown Level'}{' '}
          </Typography>
        )}
      </Box>
    );
  };

  // In the frtlProps function where the eye icon is defined
  const frtlProps = (batch, batch_id) => ({
    prop: 'frtl',
    batch_id,
    otherTextProps: {
      label: 'FRTL',
      disabled: true,
      value: getFrtlAliasString(batch?.frtl, frtlContextValue),
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                // Check if the run_status allows edits (1 or 2)
                const canEdit = values?.run_status === 1 || values?.run_status === 2;

                // Only open popup if allowed to edit
                if (canEdit) {
                  handleFrtlPopupOpen(batch_id, batch?.frtl);
                } else {
                  console.log('Editing not allowed with current run status:', values?.run_status);
                }
              }}
            >
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
  });

  const plantCountProps = (batch, batch_id) => ({
    prop: 'plant_count',
    batch_id,
    otherTextProps: {
      value: batch?.plant_count,
    },
    inputProps: {
      type: 'number',
      min: 0,
    },
  });

  const qpProps = (batch, batch_id) => {
    const selectedPhasePlan = batch?.phaseplan;

    // Use == for loose comparison to handle string/number mismatches
    const filteredQualityPlans = selectedPhasePlan
      ? values?.qualityplans?.filter(qp => qp.phaseplan == selectedPhasePlan) || []
      : [];

    return {
      prop: 'qualityplan',
      batch_id,
      otherTextProps: {
        value: batch?.qualityplan ?? '',
        select: true,
        disabled: !selectedPhasePlan,
        SelectProps: {
          IconComponent: DownArrowIcon,
        },
      },
      filteredQualityPlans,
    };
  };

  const ppProps = (batch, batch_id) => ({
    prop: 'phaseplan',
    batch_id,
    otherTextProps: {
      value: batch?.phaseplan,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => {
        // Find compatible quality plans for this new phase
        const compatibleQPs = values?.qualityplans?.filter(qp => qp.phaseplan == value) || [];
        const globalQP = values?.qualityplan;

        // Auto-select: 1. Global selection if compatible, 2. First available if only one, 3. Reset
        let nextQP = '';
        if (globalQP && compatibleQPs.some(qp => qp.id === globalQP)) {
          nextQP = globalQP;
        } else if (compatibleQPs.length === 1) {
          nextQP = compatibleQPs[0].id;
        }

        // Use atomic update to prevent race conditions and ensure both fields change
        handleBatchLevelValues(batch_id, {
          phaseplan: value,
          qualityplan: nextQP
        });
      },
    },
  });

  const mpProps = (batch, batch_id) => ({
    prop: 'mp_tag_number',
    batch_id,
    otherTextProps: {
      value: batch?.mp_tag_number ?? '',
      disabled: !(values?.run_status === 1 || values?.run_status === 2) || values?.processing,
      inputProps: {
        maxLength: 24,
      },
    },
  });

  const pidProps = (batch, batch_id) => ({
    prop: 'package_id',
    batch_id,
    otherTextProps: {
      value: batch?.package_id ?? '',
    },
  });
  const tsProps = (batch, batch_id) => {
    // Get the selected phaseplan for this batch
    const selectedPhasePlan = batch?.phaseplan;

    // Filter subphases based on the selected phaseplan
    const filteredSubphases = selectedPhasePlan
      ? phasePlans?.filter(plan => plan.id === selectedPhasePlan)?.[0]?.subphases || []
      : [];

    return {
      prop: 'target_subphase',
      batch_id,
      otherTextProps: {
        select: true,
        value: batch?.target_subphase ?? '',
        SelectProps: {
          IconComponent: DownArrowIcon,
        },
      },
      // Pass the filtered subphases to be used in the TextField
      filteredSubphases,
    };
  };


  const tsdProps = (batch, batch_id) => ({
    prop: 'target_subphase_date',
    batch_id,
    otherTextProps: {
      type: 'date',
      value: batch?.target_subphase_date ?? '',
      InputLabelProps: { shrink: true },
    },
  });

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const plantTypesList = PLANT_TYPES.map((e, i) => (
    <MenuItem key={i} value={e.value} {...cropPlanMenuItemProps}>
      {e.label}
    </MenuItem>
  ));

  const pTProps = (batch, batch_id) => ({
    prop: 'plant_type',
    batch_id,
    otherTextProps: {
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      value: batch?.plant_type,
    },
  });

  const handleStrainDelRes = id => {
    const key = isNaN(+id) ? 'tempRef' : 'id';
    handleSetValues(
      'strains',
      values?.strains?.filter(e => e?.[key] !== id)
    );
  };

  const handleDeleteStrain = strain => () => {
    if (!cropPlanPerms?.delete) return;

    if ('id' in strain) {
      let errConfig = {};
      const entityWithID = cropplanStrainEndpoint(strain?.id);
      APIService.deleteInstance(entityWithID)
        .then(handleStrainDelRes(strain?.id))
        .catch(handleErrorWithFeedback(errFeedbackFn(errConfig)));
    } else {
      handleStrainDelRes(strain?.tempRef);
    }
  };

  const handleBatchDelete = batch_id => () => {
    if (!cropPlanPerms?.delete) return;

    if (isNaN(+batch_id)) {
      handleStrainLevelValues(
        'batches',
        strain?.batches?.filter(batch => (batch?.id && batch?.id !== batch_id) || (batch?.tempRef && batch?.tempRef !== batch_id))
      );
      return;
    }

    const entityWithID = `batches/${batch_id}/`;
    const errConfig = {};

    APIService.deleteInstance(entityWithID)
      .then(() =>
        handleStrainLevelValues(
          'batches',
          strain?.batches?.filter(batch => batch?.id !== batch_id)
        )
      )
      .catch(handleErrorWithFeedback(errFeedbackFn(errConfig)));
  };

  const actionProps = batch_id => ({
    toShowDetailBtn: false,
    onDelClick: handleBatchDelete(batch_id),
  });

  const delProps = {
    toShowDetailBtn: false,
    onDelClick: handleDeleteStrain(strain),
  };

  const createTableEntries = (batch, batch_id) => {
    const name = <TextField {...textFieldProps(nameProps(batch, batch_id))} />;
    // const frtl = <TextField {...textFieldProps(frtlProps(batch, batch_id))} />;
    const frtl = batch?.frtl ? (
      <Tooltip title={getFrtlTooltipContent(batch.frtl, frtlContextValue, currDarkThemeClass)} placement="left">
        <span>
          <TextField {...textFieldProps(frtlProps(batch, batch_id))} />
        </span>
      </Tooltip>
    ) : (
      <TextField {...textFieldProps(frtlProps(batch, batch_id))} />
    );
    const plant_count = <TextField {...textFieldProps(plantCountProps(batch, batch_id))} />;

    // Quality Plan implementation with filtering
    const qualityPlanProps = qpProps(batch, batch_id);
    const filteredItems = qualityPlanProps.filteredQualityPlans.map((qp, index) => (
      <MenuItem key={index} value={qp.id} {...cropPlanMenuItemProps}>
        {qp.name}
      </MenuItem>
    ));

    const qualityplan = (
      <TextField {...textFieldProps(qualityPlanProps)}>
        {filteredItems.length > 0 ? filteredItems : (
          <MenuItem value="" disabled {...cropPlanMenuItemProps}>No Quality Plans</MenuItem>
        )}
      </TextField>
    );

    const phaseplan = <TextField {...textFieldProps(ppProps(batch, batch_id))}>{phasePlansList}</TextField>;
    const action = <DetailAndDelBtn {...actionProps(batch_id)} />;
    const plant_type = <TextField {...textFieldProps(pTProps(batch, batch_id))}>{plantTypesList}</TextField>;
    const mp_tag_number = <TextField {...textFieldProps(mpProps(batch, batch_id))} />;
    // const target_subphase = <TextField {...textFieldProps(tsProps(batch, batch_id))}>{tragetSubphasesList}</TextField>;
    // Get the target subphase props with filtered subphases
    const targetSubphaseProps = tsProps(batch, batch_id);

    // Create the subphase menu items based on the filtered subphases
    const subphaseMenuItems = targetSubphaseProps.filteredSubphases.map((subphase, index) => (
      <MenuItem key={index} value={subphase.id} {...cropPlanMenuItemProps}>
        {subphase.name}
      </MenuItem>
    ));

    const target_subphase = (
      <TextField {...textFieldProps(targetSubphaseProps)}>
        {subphaseMenuItems}
      </TextField>
    );
    const target_subphase_date = <TextField {...textFieldProps(tsdProps(batch, batch_id))} />;
    const package_id = <TextField {...textFieldProps(pidProps(batch, batch_id))} />;
    return { name, frtl, plant_count, qualityplan, phaseplan, plant_type, action, mp_tag_number, package_id, target_subphase, target_subphase_date };
  };

  const tableEntriesList = strain?.batches?.map((batch, i) => (
    <TableRow key={`row-${i}`}>
      {titles.map(title => {
        const v1 = createTableEntries(batch, batch?.id ?? batch?.tempRef);
        const value = v1?.[title.key] ?? values?.[title.key];
        return (
          <TableCell key={title.key} align={title.align ?? 'left'}>
            {value}
          </TableCell>
        );
      })}
    </TableRow>
  ));

  const metrcAlertMsg = 'METRC tag should be 24 digits';
  const hasInvalidMetrcTag = () => strain?.batches?.some(batch => batch?.mp_tag_number && batch?.mp_tag_number.length !== 24);
  const batchTable = (
    <>
      {hasInvalidMetrcTag() && (
        <Alert severity="info" sx={{ marginTop: -3.5, marginBottom: 1, position: 'sticky', top: 0, zIndex: 10 }}>
          {metrcAlertMsg}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead {...tableContainerProps}>
            <TableRow>{tableTitlesList}</TableRow>
          </TableHead>
          <TableBody>{tableEntriesList}</TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // sending the status as metrc_synced because this is also used in the routines popup
  const currStatus = cropplanid === NEW ? null : "metrc_synced";

  return (
    <Accordion {...eCPProps}>
      <AccordionSummary {...eCPSummaryProps}>
        <Typography>{strain?.strain_name ?? 'Unkown Strain'}</Typography>
        {/* //todo: convert plant count to textfield */}
        {/* <TextField {...textFieldProps(stPCProps)} /> */}
        <Box display="flex" gap={2}>
          <Typography style={{ marginRight: 10 }}>Plant Count: {plantCount}</Typography>
          {cropplanid === NEW && <DetailAndDelBtn {...delProps} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>{batchTable}</AccordionDetails>
      {frtlPopupOpen && <CropRunFRTLPopup open={frtlPopupOpen} onClose={handleFrtlPopupClose} metrcStatus={currStatus} {...frtlPopupDetailChildren} />}
    </Accordion>
  );
}
